import { Addition } from "./Addition";
import { Group, Vector3 } from "three";
import { Mesh, BufferGeometry, BufferAttribute } from 'three';
import { LoadStl, clearDuplicateVertices, loadFromArrayBuffer, flipFaceNormals } from "Utils/ViewerUtils";

export const MESH_VERTICES_NUMBER_V2 = 332;
export const SANDAL_VERTICES_NUMBER = 587;
export const INSOLE_LENGTH_MM = 248;
export const INSOLE_WIDTH_MM = 81;

export class Insole {
  //  id: string;
  side: 'right' | 'left';
  additions: Addition[];
  lowpolyBufferGeometry: BufferGeometry | null;
  mainMesh: Mesh;
  pressureImageData: ImageData | undefined;
  gcodeText: string;
  profileCurveType: ProfileCurveTemplates | null;

  bonesGroup: Group | null;
  bonesMarkers: Group | null;
  bonesFPIMarkers: Group | null;
  curvePoints: Group | null;
  liftedMesh: Mesh | null;
  footShoeMesh: Mesh | null;
  shoeInsideMarkers: Vector3[] | null;
  shoeOutsideMarkers: Vector3[] | null;

  constructor() {
    this.side = 'right';
    this.additions = [];
    this.lowpolyBufferGeometry = new BufferGeometry();
    this.mainMesh = new Mesh();
    this.liftedMesh = null;
    this.pressureImageData = undefined;
    this.gcodeText = '';
    this.profileCurveType = ProfileCurveTemplates.Generic;
    this.bonesGroup = null;
    this.bonesMarkers = null;
    this.bonesFPIMarkers = null;
    this.curvePoints = null;
    this.footShoeMesh = null;
    this.shoeInsideMarkers = null;
    this.shoeOutsideMarkers = null;
  }

  static cloneFrom(original: Insole): Insole {
    const copy = new Insole();

    copy.side = original.side;
    copy.additions = original.additions.map(add => Addition.cloneFrom(add));
    copy.lowpolyBufferGeometry = original.lowpolyBufferGeometry
      ? original.lowpolyBufferGeometry.clone()
      : null;
    copy.mainMesh = original.mainMesh ? original.mainMesh.clone() : new Mesh();
    copy.liftedMesh = null;//original.liftedMesh ? original.liftedMesh.clone() : null;
    copy.pressureImageData = original.pressureImageData
      ? new ImageData(
        new Uint8ClampedArray(original.pressureImageData.data),
        original.pressureImageData.width,
        original.pressureImageData.height
      )
      : undefined;
    copy.gcodeText = original.gcodeText;
    copy.profileCurveType = original.profileCurveType;

    copy.bonesGroup = original.bonesGroup ? original.bonesGroup.clone() : null;
    copy.bonesMarkers = original.bonesMarkers ? original.bonesMarkers.clone() : null;
    copy.bonesFPIMarkers = original.bonesFPIMarkers ? original.bonesFPIMarkers.clone() : null;
    copy.curvePoints = original.curvePoints instanceof Group && original.curvePoints.children.length > 0
      ? original.curvePoints.clone()
      : null;
    copy.footShoeMesh = original.footShoeMesh ? original.footShoeMesh.clone() : null;
    copy.shoeInsideMarkers = original.shoeInsideMarkers
      ? original.shoeInsideMarkers.map(point => point.clone())
      : null;
    copy.shoeOutsideMarkers = original.shoeOutsideMarkers
      ? original.shoeOutsideMarkers.map(point => point.clone())
      : null;

    return copy;
  }

  loadFromJson = async (json: any) => {
    // console.log("loadFromJson**/", json);
    this.side = json.side;

    await this.loadMeshes();

    const additionPromises = json.additions.map(async (additionJson: any) => {
      // console.log("additionPromises**/")
      const addition = new Addition();
      await addition.loadFromJson(additionJson);
      await addition.loadAddition();
      return addition;
    });

    this.additions = await Promise.all(additionPromises);
  }

  load2 = async (arrayBuffer: ArrayBuffer): Promise<boolean> => {
    let loadedGeometry = loadFromArrayBuffer(arrayBuffer);
    if (!loadedGeometry) {
      this.lowpolyBufferGeometry = null;
      return false;
    }
    this.lowpolyBufferGeometry = this.preprocess(loadedGeometry);
    return true;
  }

  preprocess = (loadedGeometry: BufferGeometry): BufferGeometry => {
    // const verticesBefore = loadedGeometry.attributes.position.count;
    loadedGeometry = clearDuplicateVertices(loadedGeometry);
    return loadedGeometry;
  }

  toJSON() {
    return {
      side: this.side,
      additions: this.additions.map(addition => addition.toJSON()),
    };
  }

  loadMeshes = async () => {
    let loadedGeometry = await LoadStl(`/data/insole_${this.side}.stl`);
    if (!loadedGeometry) {
      this.lowpolyBufferGeometry = null;
      return;
    }
    this.lowpolyBufferGeometry = this.preprocess(loadedGeometry);

  }

  mirror(): BufferGeometry | null {
    if (!this.lowpolyBufferGeometry) return null;

    const mirroredGeom = this.lowpolyBufferGeometry.clone();

    const pos = mirroredGeom.attributes.position as BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setX(i, -pos.getX(i));
    }

    // Negating X reverses the winding of every triangle, so normals recomputed
    // from the index as it stands point into the insole and the top surface
    // shades like its underside. flipFaceNormals puts the winding back and
    // recomputes the normals itself.
    flipFaceNormals(mirroredGeom);
    return mirroredGeom;
  }

  static GetShoeSize(len: number) {
    if (len >= 229.5 && len < 236.5) return ShoeSize.Size35;
    if (len >= 236.5 && len < 243) return ShoeSize.Size36;
    if (len >= 243 && len < 249) return ShoeSize.Size37;
    if (len >= 249 && len < 256.5) return ShoeSize.Size38;
    if (len >= 256.5 && len < 263) return ShoeSize.Size39;
    if (len >= 263 && len < 269.5) return ShoeSize.Size40;
    if (len >= 269.5 && len < 276.5) return ShoeSize.Size41;
    if (len >= 276.5 && len < 283) return ShoeSize.Size42;
    if (len >= 283 && len < 289.5) return ShoeSize.Size43;
    if (len >= 289.5 && len < 296) return ShoeSize.Size44;
    if (len >= 296 && len < 302.5) return ShoeSize.Size45;
    if (len >= 302.5 && len < 309.5) return ShoeSize.Size46;
    if (len >= 309.5) return ShoeSize.Size47;

    return ShoeSize.Size35;
  }

  static GetLenWidthByShoeSize(shoeSize: ShoeSize) {
    const s: InsoleSize = { length: INSOLE_LENGTH_MM, width: INSOLE_WIDTH_MM }
    switch (shoeSize) {
      case ShoeSize.Size35:
        s.length = 233;
        s.width = 81;
        break;
      case ShoeSize.Size36:
        s.length = 240;
        s.width = 83;
        break;
      case ShoeSize.Size37:
        s.length = 246;
        s.width = 85;
        break;
      case ShoeSize.Size38:
        s.length = 253;
        s.width = 87;
        break;
      case ShoeSize.Size39:
        s.length = 260;
        s.width = 89;
        break;
      case ShoeSize.Size40:
        s.length = 266;
        s.width = 91;
        break;
      case ShoeSize.Size41:
        s.length = 273;
        s.width = 93;
        break;
      case ShoeSize.Size42:
        s.length = 280;
        s.width = 95;
        break;
      case ShoeSize.Size43:
        s.length = 286;
        s.width = 97;
        break;
      case ShoeSize.Size44:
        s.length = 293;
        s.width = 99;
        break;
      case ShoeSize.Size45:
        s.length = 299;
        s.width = 101;
        break;
      case ShoeSize.Size46:
        s.length = 306;
        s.width = 103;
        break;
      case ShoeSize.Size47:
        s.length = 313;
        s.width = 105;
        break;
    }
    return s;
  }

}

export interface PressureImage {
  image: ImageData;
  realWidth: number;
  realHeight: number;
}

export enum InsoleType {
  Standard = 'Standard',
  PlantarFasciitis = 'Plantar Fasciitis',
  PesPlanusMild = 'Pes Planus Mild',
  PesPlanusModerate = 'Pes Planus Moderate',
  PesPlanusSevere = 'Pes Planus Severe',
  PesPlanusVerySevere = 'Pes Planus Very Severe',
  PesCavus = 'Pes Cavus',
}

export const backendInsoleTypeMap: Record<InsoleType, string> = {
  [InsoleType.Standard]: 'Standard',
  [InsoleType.PlantarFasciitis]: 'PlantarFasciitis',
  [InsoleType.PesPlanusMild]: 'PesPlanusMild',
  [InsoleType.PesPlanusModerate]: 'PesPlanusModerate',
  [InsoleType.PesPlanusSevere]: 'PesPlanusSevere',
  [InsoleType.PesPlanusVerySevere]: 'PesPlanusVerySevere',
  [InsoleType.PesCavus]: 'PesCavus'
};

export function resolveInsoleTemplateApiName(templateName: string | null | undefined): string {
  const raw = String(templateName ?? "").trim();
  if (!raw) return raw;
  if (backendInsoleTypeMap[raw as InsoleType]) return backendInsoleTypeMap[raw as InsoleType];
  const compact = raw.toLowerCase().replace(/[\s_-]/g, "");
  const fromKey = (Object.keys(InsoleType) as string[]).find((key) => key.toLowerCase() === compact);
  if (fromKey) return fromKey;
  const fromValue = (Object.values(InsoleType) as string[]).find(
    (label) => String(label).toLowerCase().replace(/[\s_-]/g, "") === compact
  );
  if (fromValue) return backendInsoleTypeMap[fromValue as InsoleType];
  return raw;
}


export enum ProfileCurveTemplates {
  Generic = 'Generic',
  Classic = 'Classic',
  Durea = 'Durea',
  Sneaker = 'Sneaker',
  Darco = 'Darco Movilo',
  Men = 'Men',
  Women = 'Women'
}

export const backendProfileCurveTypeMap: Record<ProfileCurveTemplates, string> = {
  [ProfileCurveTemplates.Generic]: 'Generic',
  [ProfileCurveTemplates.Classic]: 'Classic',
  [ProfileCurveTemplates.Durea]: 'Durea',
  [ProfileCurveTemplates.Sneaker]: 'Sneaker',
  [ProfileCurveTemplates.Darco]: 'Darco',
  [ProfileCurveTemplates.Men]: 'Men',
  [ProfileCurveTemplates.Women]: 'Women'
};

const PROFILE_CURVE_ALIASES: Record<string, ProfileCurveTemplates> = {
  generic: ProfileCurveTemplates.Generic,
  classic: ProfileCurveTemplates.Classic,
  durea: ProfileCurveTemplates.Durea,
  sneaker: ProfileCurveTemplates.Sneaker,
  darco: ProfileCurveTemplates.Darco,
  darcomovilo: ProfileCurveTemplates.Darco,
  movilo: ProfileCurveTemplates.Darco,
  men: ProfileCurveTemplates.Men,
  man: ProfileCurveTemplates.Men,
  male: ProfileCurveTemplates.Men,
  women: ProfileCurveTemplates.Women,
  woman: ProfileCurveTemplates.Women,
  female: ProfileCurveTemplates.Women,
  عمومی: ProfileCurveTemplates.Generic,
  جنریک: ProfileCurveTemplates.Generic,
  کلاسیک: ProfileCurveTemplates.Classic,
  دورآ: ProfileCurveTemplates.Durea,
  اسنیکر: ProfileCurveTemplates.Sneaker,
  دارکو: ProfileCurveTemplates.Darco,
  مردانه: ProfileCurveTemplates.Men,
  زنانه: ProfileCurveTemplates.Women,
};

export function resolveProfileCurveName(raw: string | null | undefined): ProfileCurveTemplates | undefined {
  const compact = String(raw ?? "").trim().toLowerCase().replace(/[\s_-]/g, "");
  if (!compact) return undefined;
  if (PROFILE_CURVE_ALIASES[compact]) return PROFILE_CURVE_ALIASES[compact];
  const fromValue = (Object.values(ProfileCurveTemplates) as string[]).find(
    (label) => label.toLowerCase().replace(/[\s_-]/g, "") === compact
  );
  if (fromValue) return fromValue as ProfileCurveTemplates;
  const fromKey = (Object.keys(ProfileCurveTemplates) as string[]).find((key) => key.toLowerCase() === compact);
  if (fromKey) return ProfileCurveTemplates[fromKey as keyof typeof ProfileCurveTemplates];
  return undefined;
}

export function profileCurveSelectKey(last: ProfileCurveTemplates): string {
  const entry = (Object.keys(ProfileCurveTemplates) as (keyof typeof ProfileCurveTemplates)[])
    .find((key) => ProfileCurveTemplates[key] === last);
  return entry || String(last);
}

export enum ConditionSide {
  right = 'Right',
  left = 'Left',
  both = 'Both',
  none = 'None'
}

export enum ShoeSize {
  Size35 = '35',
  Size36 = '36',
  Size37 = '37',
  Size38 = '38',
  Size39 = '39',
  Size40 = '40',
  Size41 = '41',
  Size42 = '42',
  Size43 = '43',
  Size44 = '44',
  Size45 = '45',
  Size46 = '46',
  Size47 = '47',
}

export interface InsoleSize {
  length: number,
  width: number
}
